import React, { useState, useEffect } from 'react';
import { getRelatedArticles } from '../services/articles';
import { stripMarkdownFormatting } from '../utils/markdown';
import useNavigation from '../hooks/useNavigation';
import { formatDateCompact } from '../utils/dateUtils';

const RelatedArticlesWidget = ({ articleId, currentArticle }) => {
  const [activeTab, setActiveTab] = useState('unread');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { navigateToArticle, getNavigationContext } = useNavigation();

  const [unreadArticles, setUnreadArticles] = useState([]);
  const [readArticles, setReadArticles] = useState([]);

  // Fetch related articles with dual queries for guaranteed balanced results
  // NOTE: Only depends on articleId, not currentArticle, to prevent re-fetching
  // when article read status changes (Issue #1 fix)
  useEffect(() => {
    if (!articleId) return;

    const fetchRelatedArticles = async () => {
      try {
        setLoading(true);
        setError(null);

        // Dual parallel queries for guaranteed 5 unread + 5 read
        // Prevents imbalance when user has many more read than unread articles
        const [unreadResponse, readResponse] = await Promise.all([
          getRelatedArticles(articleId, { status: 'unread', limit: 5 }),
          getRelatedArticles(articleId, { status: 'read', limit: 5 })
        ]);

        setUnreadArticles(unreadResponse.related_articles || []);
        setReadArticles(readResponse.related_articles || []);
      } catch (err) {
        console.error('Failed to fetch related articles:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchRelatedArticles();
  }, [articleId]);

  // Don't render if no article is provided or if it's still processing
  if (!currentArticle || currentArticle.processing_status === 'processing') {
    return null;
  }

  // Don't render if no related articles found
  const hasUnreadArticles = unreadArticles.length > 0;
  const hasReadArticles = readArticles.length > 0;

  if (!hasUnreadArticles && !hasReadArticles && !loading && !error) {
    return null;
  }

  const handleArticleClick = (relatedArticle) => {
    // Navigate to the related article using the centralized navigation system
    const currentContext = getNavigationContext();

    navigateToArticle(relatedArticle.id, {
      context: currentContext.context,
      preserveHistory: true
    });
  };

  const renderArticleCard = (article) => (
    <div
      key={article.id}
      onClick={() => handleArticleClick(article)}
      className="block p-4 bg-surface-alt/50 rounded-lg hover:bg-surface-hover cursor-pointer transition-colors duration-200 border border-theme-border-subtle"
    >
      <div className="flex items-start justify-between mb-2">
        <h4 className="font-medium text-heading text-sm line-clamp-2 flex-1 mr-3">
          {article.title || 'Untitled'}
        </h4>
        <div className="flex-shrink-0 text-xs text-secondary-text">
          {Math.round(article.similarity_score * 100)}%
        </div>
      </div>

      {article.summary && (
        <p className="text-sm text-secondary-text line-clamp-2 mb-2">
          {stripMarkdownFormatting(article.summary)}
        </p>
      )}

      <div className="flex items-center justify-between text-xs text-secondary-text">
        <div className="flex items-center space-x-2">
          {article.domain && (
            <span className="bg-theme-border-subtle px-2 py-1 rounded">
              {article.domain}
            </span>
          )}
          {article.author && (
            <span>by {article.author}</span>
          )}
        </div>
        <span>{formatDateCompact(article.publish_date || article.created_at)}</span>
      </div>
    </div>
  );

  const currentArticles = activeTab === 'unread' ? unreadArticles : readArticles;

  return (
    <div className="mt-8 pt-8 border-t border-theme-border-subtle">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-heading">Related Articles</h3>
      </div>

      <>
          {/* Tab Navigation */}
          {hasUnreadArticles && hasReadArticles && (
            <div className="flex space-x-1 mb-4 bg-surface-hover p-1 rounded-lg">
              <button
                onClick={() => setActiveTab('unread')}
                className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                  activeTab === 'unread'
                    ? 'bg-surface text-heading shadow-sm'
                    : 'text-secondary-text hover:text-heading'
                }`}
              >
                Unread ({unreadArticles.length})
              </button>
              <button
                onClick={() => setActiveTab('read')}
                className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                  activeTab === 'read'
                    ? 'bg-surface text-heading shadow-sm'
                    : 'text-secondary-text hover:text-heading'
                }`}
              >
                Read ({readArticles.length})
              </button>
            </div>
          )}

          {/* Content Area */}
          <div>
            {loading && (
              <div className="flex justify-center py-8">
                <div className="spinner w-6 h-6"></div>
              </div>
            )}

            {error && (
              <div className="text-center py-8">
                <p className="text-sm text-red-600">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="mt-2 text-sm text-primary-600 hover:text-primary-700"
                >
                  Try Again
                </button>
              </div>
            )}

            {!loading && !error && currentArticles.length > 0 && (
              <div className="space-y-3">
                {currentArticles.map(renderArticleCard)}
              </div>
            )}

            {!loading && !error && currentArticles.length === 0 && (
              <div className="text-center py-8 text-secondary-text">
                <svg className="w-12 h-12 mx-auto mb-3 text-tertiary-text" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-sm">
                  No {activeTab === 'unread' ? 'unread' : 'read'} related articles found
                </p>
                {hasUnreadArticles || hasReadArticles ? (
                  <p className="text-xs text-tertiary-text mt-1">
                    Try the other tab to see more articles
                  </p>
                ) : null}
              </div>
            )}
          </div>

        {/* Help Text */}
        {!loading && !error && (hasUnreadArticles || hasReadArticles) && (
          <div className="mt-4 text-xs text-secondary-text text-center">
            Related articles found using semantic similarity • Click to open
          </div>
        )}
      </>
    </div>
  );
};

export default RelatedArticlesWidget;